import { FC, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Activity } from '../../../../services'
import { SiteActivityTableRow } from './SiteActivityTableRow'
import { useSiteActivities } from './useSiteActivities'

const TABLE_HEADER_BG = '#f9f9f9'

function filterRenderableActivities(activities: Activity[]): Activity[] {
  return activities.filter(
    (activity) =>
      Boolean(activity.activityTitle?.trim()) ||
      Boolean(activity.activityDescription?.trim()) ||
      Boolean(activity.activityByName?.trim())
  )
}

export interface SiteActivitiesTabProps {
  accountId: number
}

export const SiteActivitiesTab: FC<SiteActivitiesTabProps> = ({ accountId }) => {
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down('md'))
  const { activities, loading, error, hasMore, loadMore } =
    useSiteActivities(accountId)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const rows = useMemo(
    () => filterRenderableActivities(activities),
    [activities]
  )

  useEffect(() => {
    if (!loading && hasMore && rows.length === 0 && activities.length > 0) {
      loadMore()
    }
  }, [loading, hasMore, rows.length, activities.length, loadMore])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          loadMore()
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loadMore, loading])

  if (loading && rows.length === 0 && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!loading && !error && rows.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          px: 2,
        }}
      >
        <Typography color="text.secondary">This list is empty</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 2, width: '100%' }}>
      {error && (
        <Typography color="error" sx={{ px: 2, py: 1 }}>
          {error}
        </Typography>
      )}

      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table
          size="small"
          stickyHeader
          sx={{ width: '100%', tableLayout: 'fixed' }}
          aria-label="Site activity log"
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: TABLE_HEADER_BG }}>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  width: '14%',
                  backgroundColor: TABLE_HEADER_BG,
                }}
              >
                Date & Time
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  width: '26%',
                  backgroundColor: TABLE_HEADER_BG,
                }}
              >
                Action
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  width: isCompact ? '60%' : '40%',
                  backgroundColor: TABLE_HEADER_BG,
                }}
              >
                Details
              </TableCell>
              {!isCompact && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    width: '20%',
                    backgroundColor: TABLE_HEADER_BG,
                  }}
                >
                  By
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((activity, index) => (
              <SiteActivityTableRow
                key={`${activity.id}-${index}`}
                activity={activity}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
    </Box>
  )
}

export default SiteActivitiesTab
